import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  category: string;
  country: string;
  resident_club?: string;
}

interface PlayerSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  players: Player[];
  playersWithScores: Record<string, number[]>;
  onSelectPlayer: (playerId: string) => void;
}

export default function PlayerSelectionModal({
  isOpen,
  onClose,
  players,
  playersWithScores,
  onSelectPlayer
}: PlayerSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);

  useEffect(() => {
    // Initialize filtered players with all players
    setFilteredPlayers(players);
  }, [players]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (!term.trim()) {
      setFilteredPlayers(players);
      return;
    }
    
    // Filter players based on search term
    const filtered = players.filter(player => 
      `${player.first_name} ${player.last_name}`.toLowerCase().includes(term) ||
      player.country.toLowerCase().includes(term) ||
      (player.resident_club && player.resident_club.toLowerCase().includes(term))
    );
    
    setFilteredPlayers(filtered);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      Select Player
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={onClose}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-4 relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name, country, or club..."
                      value={searchTerm}
                      onChange={handleSearch}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
                    />
                  </div>

                  {filteredPlayers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {searchTerm ? (
                        <p>No players found matching "{searchTerm}"</p>
                      ) : (
                        <p>No players available</p>
                      )}
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto">
                      {filteredPlayers.map((player) => (
                        <div
                          key={player.id}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between gap-3 border-b border-gray-100"
                        >
                          <div>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                                <UserIcon className="h-6 w-6 text-golf-orange" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {player.first_name} {player.last_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {player.category} • {player.country}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            {/* Show which rounds the player already has scores for */}
                            {playersWithScores[player.id]?.length > 0 ? (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {playersWithScores[player.id].map(round => (
                                  <span key={round} className="px-2 py-0.5 text-xs bg-gray-100 rounded-full">
                                    Round {round}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            <button
                              onClick={() => {
                                onSelectPlayer(player.id);
                                onClose();
                              }}
                              className="px-3 py-1 text-sm bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90"
                            >
                              Add Score
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}